import React, { useEffect, useState } from 'react'
import 'd3-selection-multi'
import * as config from './config'
import './styles.scss'
import '../globals.scss'
import { localesHospitalMultiples as localesDE } from '../locales'
import { localesHospitalMultiples as localesFR } from '../locales-fr'
import { LineOnly } from 'components/Charts_LineOnly'
import { chunkArray } from '../globalDrawFunctions'
import csvjson from 'csvjson'
import { RangeSlider } from 'components/RangeSlider'

import {
  Chart,
  Key,
  MultiLine,
  addDays,
  calcPeaksInRanges
} from 'components/ChartToolkit'
const isMobile = config.isMobile
const waves = [
  { start: '2020-02-01', end: '2020-05-31' },
  { start: '2020-06-01', end: '2021-02-28' },
  { start: '2021-03-01', end: '2021-06-30' },
  { start: '2021-07-01', end: '2021-09-30' },
  { start: '2021-10-01', end: '2022-02-29' },
  { start: '2022-03-01', end: '2022-09-30' },
  { start: '2022-10-01', end: new Date().toISOString().split('T')[0] }
]
const wavesCantons = [
  { start: '2020-02-01', end: '2021-02-28' },
  { start: '2021-03-01', end: '2021-09-30' },
  { start: '2021-10-01', end: '2022-06-30' },
  { start: '2022-07-01', end: new Date().toISOString().split('T')[0] }
]
const formatDate = (datestring: string) => {
  const parts = datestring.split('.').map(Number)
  return (
    parts[2] +
    '-' +
    (parts[1] < 10 ? '0' + parts[1] : parts[1]) +
    '-' +
    (parts[0] < 10 ? '0' + parts[0] : parts[0])
  )
}

export const HospitalMultiples = ({
  showLink = true,
  showCantons = true,
  showHeader = true,
  locale = 'de'
}: {
  capacityPeak?: [number, string]
  showLink?: boolean
  showCantons?: boolean
  showHeader?: boolean
  locale?: 'de' | 'fr'
}) => {
  const [drawData, setDrawData] = useState<cantonsData[][]>()
  const [swiss, setSwissWideData] = useState<BasicDataType[][]>()
  const [swissPeaks, setSwissPeaks] = useState<
    { d: string; v: number; t: string }[][]
  >()
  const [showall, setShowAll] = useState<boolean>(false)
  const [xDomain, setXDomain] = useState<Date[]>([
    new Date('2021-07-01'),
    addDays(200, new Date())
  ])
useEffect(()=>{
console.log(xDomain.map(m=>new Date().toISOString().split("T")[0]))

},[xDomain])
  const locales = locale === 'de' ? localesDE : localesFR

  useEffect(() => {
    Promise.all([
      fetch(
        'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/cantons-details-hosp_till_01052023.csv'
      )
        .then(response => response.text())
        .then(csv => csvjson.toObject(csv, {})),
      fetch(
        'https://storage.googleapis.com/ta-interaktiv/corona-dashboard-dev/BAG_Hospitalisation_Meta_till_01052023.csv?a=' +
          Math.random()
      )
        .then(response => response.text())
        .then(csv => csvjson.toObject(csv, {})),
      ,
      fetch(
        'https://storage.googleapis.com/ta-interaktiv/corona-dashboard/switzerland-hospitalized_till_01052023.json'
      ).then(response => response.json())
      // .then(csv => csvjson.toObject(csv, {}))
    ]).then(results => {

      const hosp = Object.values(results[3].hospitalized)
        .filter(f => f.val)
        .map(m => ({ d: m.date, v: +m.val }))
        .filter(f => !isNaN(+f.v))

      const intense = Object.entries(results[3].intensiveCare)
        .map(m => ({ d: m[0], v: +m[1].val }))
        .filter(f => f.v)

      setSwissWideData([hosp, intense])

      setSwissPeaks([
        calcPeaksInRanges(hosp, waves).filter(
          // Filter last 21 days to get no peak at the end
          f => new Date(f.d) < addDays(-21, new Date())
        ),
        calcPeaksInRanges(intense, waves).filter(
          // Filter last 21 days to get no peak at the end
          f => new Date(f.d) < addDays(-21, new Date())
        )
      ])
      const cantons = results[1].map(m => {
        m.data = results[0]
          .map(m => {
            m.covid_in_hospital = +m.covid_in_hospital
            m.covid_on_icu = +m.covid_on_icu
            return m
          })
          .filter(f => f.canton === m.canton)

        return {
          abr: m.canton,
          cases: m.totalCases,
          data: m.data,
          start: m.start
        }
      })
      setDrawData(
        chunkArray(
          cantons
            .filter(f => config.cantonsToHide.indexOf(f.abr) === -1)
            .sort((a, b) => +b.cases - +a.cases),
          config.colCount
        )
      )
    })
  }, [])
  return (
    <div
      className={'ui vertical segment ' + (!showCantons ? 'emb' : '')}
      style={{ overflow: 'visible', padding: showHeader ? undefined : '0' }}
    >
      {drawData && (
        <>
          {/* <div className='chart' style={{ width: '100%' }}> */}
          <div className={isMobile ? '' : 'ui text container'}>
            <Chart
              title={showHeader ? locales.title : undefined}
              sub={showCantons && showHeader ? locales.subline : undefined}
            >
              <>
                {showLink && <div className='link'>{locales.link}</div>}
                <Key
                  entries={[
                    { type: 'line', label: locales.keyHosp, color: '#2CA29F' },
                    {
                      type: 'line',
                      label: locales.keyIntensive,
                      color: '#D3343F'
                    }
                  ]}
                />

                <div className='chartMeta big'>
                  <div className='name'>{locales.swissTitle}</div>
                  {swiss && (
                    <MultiLine
                      data={[
                        swiss[0].filter(
                          f =>
                            new Date(f.d) >= new Date(xDomain[0]) &&
                            new Date(f.d) <= new Date(xDomain[1])
                        ),
                        swiss[1].filter(
                          f =>
                            new Date(f.d) >= new Date(xDomain[0]) &&
                            new Date(f.d) <= new Date(xDomain[1])
                        )
                      ]}
                      yDomain={[4000, 0]}
                      xDomain={xDomain}
                      yTicks={{ text: [4000], lines: [0, 2000, 4000] }}
                      xTicks={{
                        text: ['2020-02-01', '2021-01-01', '2021-12-01'].filter(
                          f =>
                            new Date(f) >= new Date(xDomain[0]) &&
                            new Date(f) <= new Date(xDomain[1])
                        ),
                        lines: [
                          '2020-02-01',
                          '2021-01-01',
                          '2021-12-01'
                        ].filter(
                          f =>
                            new Date(f) >= new Date(xDomain[0]) &&
                            new Date(f) <= new Date(xDomain[1])
                        )
                      }}
                      className='swisswide'
                      height={300}
                      annotations={swissPeaks}
                    />
                  )}
                </div>
                <div className='optionsWrapper'>
                  <RangeSlider
                    labels={locales.timerange}
                    lang={locale + '-CH'}
                    change={setXDomain}
                    initial={xDomain}
                  />
                </div>
              </>
            </Chart>{' '}
          </div>
          {/* </div> */}
          {showCantons && (
            <div className='cantonsMultiples'>
              <div className=''>
                <div className='personalGrid'>
                  {drawData.map((drawRow: cantonsData[], rnr: number) => {
                    if (rnr > (isMobile ? 1 : 0) && !showall) {
                      return null
                    }
                    return (
                      <React.Fragment key={rnr}>
                        {drawRow.map((e: cantonsData, i: number) => {
                          // e.data.pop()
                          const maxHosp = Math.max(
                            ...e.data.map(o => o.covid_in_hospital),
                            0
                          )
                          const maxInte = Math.max(
                            ...e.data.map(o => o.covid_on_icu),
                            0
                          )

                          const rHosp = 100
                          let scaleMaxHosp =
                            Math.ceil((maxHosp * 1.2) / rHosp) * rHosp
                          if (scaleMaxHosp === 0) scaleMaxHosp = rHosp

                          const rInte = 10
                          let scaleMaxInte =
                            Math.ceil((maxInte * 1.2) / rInte) * rInte
                          if (scaleMaxInte === 0) scaleMaxInte = rInte

                          const hospPeaks = wavesCantons.map(wave => {
                            const waveData = e.data.filter(
                              m =>
                                new Date(m.date) >= new Date(wave.start) &&
                                new Date(m.date) <= new Date(wave.end)
                            )
                            const wavemax = Math.max(
                              ...waveData.map(m => +m.covid_in_hospital)
                            )

                            const peakpos = waveData.findIndex(
                              f => +f.covid_in_hospital === wavemax
                            )
                            return [
                              {
                                d: waveData[peakpos].date,
                                v: waveData[peakpos].covid_in_hospital,
                                t: '' + waveData[peakpos].covid_in_hospital
                              }
                            ]
                          })
                          const intensePeaks = wavesCantons
                            .map(wave => {
                              const waveData = e.data.filter(
                                m =>
                                  new Date(m.date) >= new Date(wave.start) &&
                                  new Date(m.date) <= new Date(wave.end)
                              )
                              const wavemax = Math.max(
                                ...waveData.map(m => +m.covid_on_icu)
                              )

                              const peakpos = waveData.findIndex(
                                f => +f.covid_on_icu === wavemax
                              )
                              return [
                                {
                                  d: waveData[peakpos].date,
                                  v: waveData[peakpos].covid_on_icu,
                                  t: '' + waveData[peakpos].covid_on_icu
                                }
                              ]
                            })
                            .filter(f => +f[0].v > 0)
                            console.log(e.data.length)

                          let hospdata = e.data
                            // .filter(f => +f.covid_in_hospital > 0)
                            .map(m => ({
                              d: m.date,
                              v: +m.covid_in_hospital
                            }))
                            // .sort((a, b) => +new Date(a.d) - +new Date(b.d))
                            .filter(
                              f =>
                                new Date(f.d) >= new Date(xDomain[0]) &&
                                new Date(f.d) <= new Date(xDomain[1])
                            )
                          let intensedata = e.data
                            // .filter(f => +f.covid_on_icu > 0)
                            .map(m => ({
                              d: m.date,
                              v: +m.covid_on_icu
                            }))
                            // .sort((a, b) => new Date(a.d) - new Date(b.d))
                            .filter(
                              f =>
                                new Date(f.d) >= new Date(xDomain[0]) &&
                                new Date(f.d) <= new Date(xDomain[1])
                            )

                          return (
                            <React.Fragment key={i}>
                              <div
                                className={'col' + (rnr === 0 ? ' first' : '')}
                                key={i}
                              >
                                <div className='chartMeta'>
                                  <div className='name'>{e.abr}</div>
                                </div>

                                <MultiLine
                                  data={[hospdata]}
                                  yDomain={[scaleMaxHosp, 0]}
                                  xDomain={xDomain}
                                  yTicks={{
                                    text: [scaleMaxHosp],
                                    lines: [0, scaleMaxHosp / 2, scaleMaxHosp]
                                  }}
                                  xTicks={{
                                    text: [
                                      hospdata[0].d,
                                      hospdata[hospdata.length - 1].d
                                    ],
                                    lines: [
                                      hospdata[0].d,
                                      hospdata[hospdata.length - 1].d
                                    ]
                                  }}
                                  className='covid_in_hospital'
                                  height={150}
                                  annotations={hospPeaks}
                                />
                                {intensedata.length > 0 && (
                                  <MultiLine
                                    data={[intensedata]}
                                    yDomain={[scaleMaxInte, 0]}
                                    xDomain={xDomain}
                                    yTicks={{
                                      text: [scaleMaxInte],
                                      lines: [0, scaleMaxInte / 2, scaleMaxInte]
                                    }}
                                    xTicks={{
                                      text: [
                                        intensedata[0].d,
                                        intensedata[intensedata.length - 1].d
                                      ].filter(
                                        f =>
                                          new Date(f) >= new Date(xDomain[0]) &&
                                          new Date(f) <= new Date(xDomain[1])
                                      ),
                                      lines: [
                                        intensedata[0].d,
                                        intensedata[intensedata.length - 1].d
                                      ].filter(
                                        f =>
                                          new Date(f) >= new Date(xDomain[0]) &&
                                          new Date(f) <= new Date(xDomain[1])
                                      )
                                    }}
                                    className='covid_on_icu'
                                    height={150}
                                    annotations={intensePeaks}
                                  />
                                )}
                              </div>
                            </React.Fragment>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </div>
              </div>
              <div className='ui container showMoreWrapper'>
                <div className='ui text container'>
                  <div
                    className='showMore'
                    onClick={() => {
                      setShowAll(!showall)
                    }}
                  >
                    <i
                      className={`ui icon chevron ${!showall ? 'right' : 'up'}`}
                    />
                    {!showall ? locales.showMoreClosed : locales.showMoreOpened}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
export default HospitalMultiples
